import { useEffect, useMemo, useState } from 'react';
import { fetchUsers, UserData } from '../services/userService';

export interface UsersLookup {
  users: UserData[];
  byId: Map<string, UserData>;
  loading: boolean;
  refresh: () => void;
}

let cachedUsers: UserData[] | null = null;
let inFlight: Promise<UserData[]> | null = null;
const subscribers = new Set<(users: UserData[]) => void>();

const load = (force?: boolean): Promise<UserData[]> => {
  if (force) { cachedUsers = null; inFlight = null; }
  if (cachedUsers) return Promise.resolve(cachedUsers);
  if (!inFlight) {
    inFlight = fetchUsers().then((users) => {
      cachedUsers = users;
      inFlight = null;
      subscribers.forEach((cb) => cb(users));
      return users;
    });
  }
  return inFlight;
};

/** Fetches the real user list once per session (module-level cache, deduped in-flight
 * fetch) so mounting several UserPickers at once — e.g. the AE/CSAM/Sponsor "Edit Team"
 * modal — only triggers a single GraphQL call. */
export const useUsersLookup = (): UsersLookup => {
  const [users, setUsers] = useState<UserData[]>(cachedUsers || []);
  const [loading, setLoading] = useState(!cachedUsers);

  useEffect(() => {
    subscribers.add(setUsers);
    if (!cachedUsers) {
      setLoading(true);
      load().then((u) => { setUsers(u); setLoading(false); });
    }
    return () => { subscribers.delete(setUsers); };
  }, []);

  const byId = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const refresh = () => { setLoading(true); load(true).then((u) => { setUsers(u); setLoading(false); }); };

  return { users, byId, loading, refresh };
};
